import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

import DataTable from './DataTable';
import FormModal from './FormModal';
import { getDestinations, createDestination, updateDestination, deleteDestination } from '../../../services/travelInventoryService';

const DestinationsManager = ({ searchQuery }) => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadDestinations();
  }, []);

  const loadDestinations = async () => {
    setLoading(true);
    try {
      const data = await getDestinations();
      setDestinations(data || []);
    } catch (error) {
      console.error('Error loading destinations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this destination?')) return;
    
    try {
      await deleteDestination(id);
      await loadDestinations();
    } catch (error) {
      console.error('Error deleting destination:', error);
      alert('Failed to delete destination');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await updateDestination(editingItem?.id, formData);
      } else {
        await createDestination(formData);
      }
      setIsModalOpen(false);
      await loadDestinations();
    } catch (error) {
      console.error('Error saving destination:', error);
      throw error;
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'country', label: 'Country', sortable: true },
    { key: 'destination_type', label: 'Type', sortable: true },
    { key: 'is_featured', label: 'Featured', type: 'boolean' },
    { key: 'popularity_score', label: 'Popularity', sortable: true },
  ];

  const formFields = [
    { name: 'name', label: 'Destination Name', type: 'text', required: true },
    { name: 'country', label: 'Country', type: 'text', required: true },
    { name: 'region', label: 'Region', type: 'text' },
    {
      name: 'destination_type',
      label: 'Type',
      type: 'select',
      required: true,
      options: [
        { value: 'city', label: 'City' },
        { value: 'beach', label: 'Beach' },
        { value: 'mountain', label: 'Mountain' },
        { value: 'countryside', label: 'Countryside' },
        { value: 'island', label: 'Island' },
        { value: 'cultural', label: 'Cultural' },
        { value: 'adventure', label: 'Adventure' },
      ],
    },
    { name: 'description', label: 'Description', type: 'textarea', required: true },
    { name: 'short_description', label: 'Short Description', type: 'textarea' },
    { name: 'image_url', label: 'Destination Image', type: 'image-upload' },
    { name: 'image_alt', label: 'Image Alt Text', type: 'text' },
    { name: 'latitude', label: 'Latitude', type: 'number', step: '0.0000001' },
    { name: 'longitude', label: 'Longitude', type: 'number', step: '0.0000001' },
    { name: 'timezone', label: 'Timezone', type: 'text' },
    { name: 'currency', label: 'Currency', type: 'text' },
    { name: 'language', label: 'Language', type: 'text' },
    { name: 'best_time_to_visit', label: 'Best Time to Visit', type: 'text' },
    { name: 'average_temperature', label: 'Average Temperature (°C)', type: 'number', step: '0.1' },
    { name: 'is_featured', label: 'Featured', type: 'checkbox' },
    { name: 'popularity_score', label: 'Popularity Score (0-100)', type: 'number', min: 0, max: 100 },
  ];

  const filteredData = destinations?.filter((item) => {
    const matchesSearch = searchQuery
      ? item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.country?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      : true;
    const matchesFilter = filterType === 'all' || item?.destination_type === filterType;
    return matchesSearch && matchesFilter;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Destinations</h2>
          <p className="text-gray-600 mt-1">Manage travel destinations and locations</p>
        </div>
        <Button iconName="Plus" onClick={handleCreate}>
          Add Destination
        </Button>
      </div>

      <div className="mb-4">
        <Select
          value={filterType}
          onChange={setFilterType}
          options={[
            { value: 'all', label: 'All Types' },
            { value: 'city', label: 'City' },
            { value: 'beach', label: 'Beach' },
            { value: 'mountain', label: 'Mountain' },
            { value: 'island', label: 'Island' },
            { value: 'cultural', label: 'Cultural' },
          ]}
          className="max-w-xs"
        />
      </div>

      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        title={editingItem ? 'Edit Destination' : 'Add Destination'}
        fields={formFields}
        initialData={editingItem}
      />
    </div>
  );
};

export default DestinationsManager;